import { Injectable } from '@angular/core';

import { finalize } from 'rxjs/operators';

import { NgxSpinnerService } from 'ngx-spinner';
import { NzModalService } from 'ng-zorro-antd/modal';

import { AppOptions } from '../../../core/interfaces';
import { ErrorsHandlingService } from '../../../core/services';
import { DeleteDialogComponent } from '../../../shared/components';

import { MainDashboardInterface } from '../models';
import { MainService } from '../services';

@Injectable({
  providedIn: 'root'
})
export class MainTableService {
  public sortOrderDSP = 'desc';
  public sortByDSP = 'name';

  public sortOrderSSP = 'desc';
  public sortBySSP = 'name';

  constructor(
    private loadingService: NgxSpinnerService,
    private mainService: MainService,
    private errorsHandlingService: ErrorsHandlingService,
    private modalService: NzModalService,
  ) { }

  public isOptionActiveHandler(attr: string, side: string, options: AppOptions): boolean {
    return this.isActiveOption(attr, side, options);
  }

  public getMarginByTypeHandler(endpoint: MainDashboardInterface.IEndpointsDataForMainTable): number | string | void {
    switch (endpoint.margin_type) {
      case 'fixed':
        return endpoint.margin;
      case 'adaptive':
        return 'adaptive';
      case 'range':
        return `${endpoint.min_margin} - ${endpoint.max_margin}`;
    }
  }

  public isClosedCompanyHandler(i: number, companiesState: { [key: string]: boolean }): 'none' | 'visible' {
    if (companiesState[i]) {
      return 'visible';
    }
    return 'none';
  }

  public onSortDSPTable(sortBy: string, sortableColumns: string[]): {
    sortBy: string;
    sortOrder: string;
  } | void {
    if (sortableColumns.indexOf(sortBy) === -1) {
      return;
    }
    if (this.sortByDSP === sortBy) {
      this.sortOrderDSP = this.sortOrderDSP === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortByDSP = sortBy;
      this.sortOrderDSP = 'desc';
    }

    const sortState = {
      sortBy: sortBy,
      sortOrder: this.sortOrderDSP,
    };

    return sortState;
  }

  public onSortSSPTable(sortBy: string, sortableColumns: string[]): {
    sortBy: string;
    sortOrder: string;
  } | void {
    if (sortableColumns.indexOf(sortBy) === -1) {
      return;
    }
    if (this.sortBySSP === sortBy) {
      this.sortOrderSSP = this.sortOrderSSP === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBySSP = sortBy;
      this.sortOrderSSP = 'desc';
    }

    const sortState = {
      sortBy: sortBy,
      sortOrder: this.sortOrderSSP,
    };

    return sortState;
  }

  public onDeactivateCompanyHandler(
    companyId: number,
    type: string,
    company: MainDashboardInterface.ICompaniesDataForMainTable,
    callback: (arg: { side: string }) => void,
  ): void {
    const dialogDeactivate = this.modalService.create({
      nzContent: DeleteDialogComponent,
      nzMaskClosable: false,
      nzClassName: 'delete-modal',
      nzWrapClassName: 'delete-modal-wrap',
      nzStyle: { width: '360px', height: '120px' },
      nzBodyStyle: { width: '360px', padding: '15px', height: '100%' },
      nzCentered: true,
      nzData: {
        title: 'Warning',
        message: 'Are you sure you would like to deactivate all the connections?',
        buttonSaveText: 'Yes',
        buttonCancelText: 'Cancel'
      }
    });
    dialogDeactivate.afterClose.subscribe(res => {
      switch (res) {
        case true:
          company.active = !company.active;
          this.loadingService.show();
          this.mainService.toggleEndpointsStatus(companyId, Number(company.active), type)
            .pipe(finalize(() => this.loadingService.hide()))
            .subscribe(
              () => {
                this.errorsHandlingService.showNotification('success', 'success', 'success', 'Company state updated', null);
                callback({ side: type });
              },
              (error) => {
                this.errorsHandlingService.handleError(error);
              },
            );
          break;
        case false:
          company.active = company.active;
          break;
      }
    });
  }

  public onActivateCompanyHandler(
    companyId: number,
    type: string,
    company: MainDashboardInterface.ICompaniesDataForMainTable,
    callback: (arg: { side: string }) => void,
  ): void {
    const dialogActivate = this.modalService.create({
      nzContent: DeleteDialogComponent,
      nzMaskClosable: false,
      nzClassName: 'delete-modal',
      nzWrapClassName: 'delete-modal-wrap',
      nzStyle: { width: '360px', height: '120px' },
      nzBodyStyle: { width: '360px', padding: '15px', height: '100%' },
      nzCentered: true,
      nzData: {
        title: 'Warning',
        message: 'Are you sure you would like to activate all the connections?',
        isWarningMessage: true,
        endpointsLength: company[`${type}_settings`].length,
        buttonSaveText: 'Yes',
        buttonCancelText: 'Cancel',
        warningMessage: `By activating this company you will activate
          ${company[`${type}_settings`].length} amount of
          endpoints.
          If you activate more than 50 endpoints at the same time - it might cause server issues and affect trading activity
          of your platform`,
        fontWeight: '700px',
      }
    });
    dialogActivate.afterClose.subscribe(res => {
      switch (res) {
        case true:
          company.active = !company.active;
          this.loadingService.show();
          this.mainService.toggleEndpointsStatus(companyId, Number(company.active), type)
            .pipe(finalize(() => this.loadingService.hide()))
            .subscribe(
              () => {
                this.errorsHandlingService.showNotification('success', 'success', 'success', 'Company state updated', null);
                callback({ side: type });
              },
              (error) => {
                this.errorsHandlingService.handleError(error);
              },
            );
          break;
        case false:
          company.active = company.active;
          break;
      }
    });
  }

  private isActiveOption(attr: string, side: string, options: AppOptions): boolean {
    const option = options[side].find((e) => {
      return e.attr === attr;
    });

    if (!option) {
      return false;
    }

    return option.active === true;
  }
}
